import type { QueryClient } from '@tanstack/react-query';
import { useFsAdjustStore } from '@/store/audit/fsAdjustStore';

export type FsAdjustmentRefreshMark = {
    companyId: number;
    annualId: number;
    ts: number;
};

export const FS_ADJUSTMENT_REFRESH_MARK_KEY = 'FS_ADJUSTMENT_REFRESH_MARK_V1';

/** Dispatched after CompanyAnnual update so a mounted FsAdjustment can refetch immediately. */
export const FS_ADJUSTMENT_STALE_EVENT = 'fs-adjustment-cache-stale';

/**
 * Dispatched after working-paper/saveOrUpdate succeeds.
 * Mounted FsAdjustment should background-refetch BS/PL without Skeleton / store clear
 * (WP dialogs now stay open after save).
 */
export const FS_ADJUSTMENT_SILENT_REFRESH_EVENT = 'fs-adjustment-silent-refresh';

export type FsAdjustmentStaleDetail = {
    companyId: number;
    annualId: number;
};

export type FsAdjustmentSilentRefreshDetail = {
    companyId: number;
    annualId: number;
    code?: string;
};

type CoalescedListRefreshSlot = {
    timer: ReturnType<typeof setTimeout> | null;
    inFlight: Promise<void> | null;
    waiters: Array<{ resolve: () => void; reject: (reason?: unknown) => void }>;
};

const coalescedListRefreshByKey = new Map<string, CoalescedListRefreshSlot>();

/**
 * 合并短时间内的多次无感 list 刷新：debounce + in-flight 复用，保证 burst 内 queryAdjustmentList 只请求一次。
 * WHY: WP save 常同时走 notify / setDataSaved / invalidateQueries；不合并会连打 list。
 */
export function scheduleCoalescedAdjustmentListRefresh(
    companyId: number,
    annualId: number,
    refetchList: () => Promise<unknown>,
    debounceMs = 16,
): Promise<void> {
    const cid = Number(companyId || 0);
    const aid = Number(annualId || 0);
    if (!cid || !aid) {
        return Promise.resolve();
    }

    const key = `${cid}:${aid}`;
    let slot = coalescedListRefreshByKey.get(key);
    if (!slot) {
        slot = {timer: null, inFlight: null, waiters: []};
        coalescedListRefreshByKey.set(key, slot);
    }

    return new Promise<void>((resolve, reject) => {
        slot!.waiters.push({resolve, reject});
        if (slot!.timer != null) {
            clearTimeout(slot!.timer);
        }
        slot!.timer = setTimeout(() => {
            slot!.timer = null;
            const waiters = slot!.waiters.splice(0, slot!.waiters.length);
            const settleAll = (fn: (w: { resolve: () => void; reject: (reason?: unknown) => void }) => void) => {
                waiters.forEach(fn);
            };

            const runRefetch = async () => {
                if (slot!.inFlight) {
                    await slot!.inFlight;
                    return;
                }
                slot!.inFlight = refetchList()
                    .then(() => undefined)
                    .finally(() => {
                        slot!.inFlight = null;
                    });
                await slot!.inFlight;
            };

            void runRefetch()
                .then(() => settleAll((w) => w.resolve()))
                .catch((err) => settleAll((w) => w.reject(err)));
        }, debounceMs);
    });
}

/**
 * Notify mounted FsAdjustment to silently refresh after WP saveOrUpdate.
 * Does not clear store or write sessionStorage marks (unlike STALE / Modify paths).
 */
export function notifyFsAdjustmentSilentRefresh(
    companyId?: number | null,
    annualId?: number | null,
    code?: string | null,
) {
    const cid = Number(companyId || 0);
    const aid = Number(annualId || 0);
    if (!cid || !aid) return;

    try {
        const codeKey = String(code || '').trim();
        if (codeKey) {
            useFsAdjustStore.getState().clearWpCache(codeKey);
        }
    } catch {
        // ignore store access failures
    }

    try {
        if (typeof window === 'undefined') return;
        window.dispatchEvent(
            new CustomEvent<FsAdjustmentSilentRefreshDetail>(FS_ADJUSTMENT_SILENT_REFRESH_EVENT, {
                detail: {
                    companyId: cid,
                    annualId: aid,
                    code: code ? String(code) : undefined,
                },
            }),
        );
    } catch {
        // ignore event dispatch failures
    }
}

export const readFsAdjustmentRefreshMark = (): FsAdjustmentRefreshMark | null => {
    try {
        if (typeof window === 'undefined') return null;
        const raw = window.sessionStorage.getItem(FS_ADJUSTMENT_REFRESH_MARK_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const companyId = Number(parsed?.companyId || 0);
        const annualId = Number(parsed?.annualId || 0);
        const ts = Number(parsed?.ts || 0);
        if (!companyId || !annualId || !ts) return null;
        return { companyId, annualId, ts };
    } catch {
        return null;
    }
};

export const writeFsAdjustmentRefreshMark = (mark: FsAdjustmentRefreshMark) => {
    try {
        if (typeof window === 'undefined') return;
        window.sessionStorage.setItem(FS_ADJUSTMENT_REFRESH_MARK_KEY, JSON.stringify(mark));
    } catch {
        // ignore storage failures
    }
};

export const clearFsAdjustmentRefreshMark = () => {
    try {
        if (typeof window === 'undefined') return;
        window.sessionStorage.removeItem(FS_ADJUSTMENT_REFRESH_MARK_KEY);
    } catch {
        // ignore storage failures
    }
};

/**
 * 在 CompanyAnnual 更新成功后调用：
 * - 标记同 companyId/annualId 的 FsAdjustment 需刷新（sessionStorage，供下次进入读取）
 * - 若当前 fsAdjustStore 正是该年，则清空 zustand 缓存
 * - 失效 React Query 的 queryAdjustmentList / workingRecord
 * - 派发事件，已挂载的 FsAdjustment 立即 refetch
 */
export function invalidateFsAdjustmentCachesForAnnual(
    companyId: number,
    annualId: number,
    queryClient?: QueryClient | null,
) {
    const cid = Number(companyId || 0);
    const aid = Number(annualId || 0);
    if (!cid || !aid) return;

    writeFsAdjustmentRefreshMark({ companyId: cid, annualId: aid, ts: Date.now() });

    try {
        const record = useFsAdjustStore.getState().adjustRecord as
            | { companyId?: number; annualId?: number }
            | null
            | undefined;
        if (
            record &&
            Number(record.companyId) === cid &&
            Number(record.annualId) === aid
        ) {
            useFsAdjustStore.getState().clear();
        }
    } catch {
        // ignore store access failures
    }

    if (queryClient) {
        queryClient.invalidateQueries({ queryKey: ['adjustment', 'list', cid, aid] });
        queryClient.invalidateQueries({ queryKey: ['adjustment', 'workingRecord', cid, aid] });
        queryClient.invalidateQueries({ queryKey: ['rounding-adjust', aid] });
    }

    try {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(
                new CustomEvent<FsAdjustmentStaleDetail>(FS_ADJUSTMENT_STALE_EVENT, {
                    detail: { companyId: cid, annualId: aid },
                }),
            );
        }
    } catch {
        // ignore event dispatch failures
    }
}

/**
 * Modify 成功后调用：彻底清除该年度的前端缓存（离开页面前使用）。
 * - 清空 fsAdjustStore（含 wpDetailsCache / roundingGroup）
 * - removeQueries 删除 React Query 内存数据（非仅 invalidate，避免仍挂载时 refetch 写回）
 * - 写入 sessionStorage 刷新标记，供下次进入强制拉新
 * - 不派发 STALE 事件，避免离开前再次灌入缓存
 */
export function clearFsAdjustmentCachesForAnnual(
    companyId: number,
    annualId: number,
    queryClient?: QueryClient | null,
) {
    const cid = Number(companyId || 0);
    const aid = Number(annualId || 0);
    if (!cid || !aid) return;

    writeFsAdjustmentRefreshMark({ companyId: cid, annualId: aid, ts: Date.now() });

    try {
        useFsAdjustStore.getState().clear();
    } catch {
        // ignore store access failures
    }

    if (queryClient) {
        // 先取消进行中的请求，再删除缓存，避免离开瞬间 refetch 写回
        void queryClient.cancelQueries({ queryKey: ['adjustment', 'list', cid, aid] });
        void queryClient.cancelQueries({ queryKey: ['adjustment', 'workingRecord', cid, aid] });
        void queryClient.cancelQueries({ queryKey: ['rounding-adjust', aid] });
        queryClient.removeQueries({ queryKey: ['adjustment', 'list', cid, aid] });
        queryClient.removeQueries({ queryKey: ['adjustment', 'workingRecord', cid, aid] });
        queryClient.removeQueries({ queryKey: ['rounding-adjust', aid] });
    }
}
